import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { UploadCloud, X, Loader2 } from "lucide-react";

const BACKEND_URL = API.replace(/\/api$/, "");

// Comprime en el navegador: redimensiona a máx. 1920px y recodifica a WebP 0.82.
// Mantiene buena nitidez sin pasarse. Los GIF se dejan intactos (animación).
async function compressImage(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  const MAX = 1920;
  const QUALITY = 0.82;
  try {
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    if (Math.max(width, height) > MAX) {
      const scale = MAX / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/webp", QUALITY));
    if (!blob || blob.size >= file.size) return file;
    const base = (file.name || "imagen").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.webp`, { type: "image/webp" });
  } catch {
    return file;
  }
}

export function resolveImageUrl(url) {
  if (!url) return "";
  const idx = url.indexOf("/api/files/");
  if (idx !== -1) return `${BACKEND_URL}${url.slice(idx)}`;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

// Genera un srcset responsivo (variantes redimensionadas bajo demanda) para imágenes subidas.
const SRCSET_WIDTHS = [200, 300, 400, 600, 800];
const HERO_SRCSET_WIDTHS = [400, 800, 1200];
export function buildSrcSet(url, hero = false) {
  if (!url || !url.includes("/api/files/")) return undefined;
  const abs = resolveImageUrl(url);
  const sep = abs.includes("?") ? "&" : "?";
  return (hero ? HERO_SRCSET_WIDTHS : SRCSET_WIDTHS).map((w) => `${abs}${sep}w=${w} ${w}w`).join(", ");
}

export function ImageUpload({ value, onChange, label = "Imagen", testid = "image-upload", altValue, onAltChange, altPlaceholder = "Describe la imagen (accesibilidad y SEO)" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona un archivo de imagen");
      return;
    }
    setUploading(true);
    try {
      const optimized = await compressImage(file);
      const fd = new FormData();
      fd.append("file", optimized);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      const saved = file.size - optimized.size;
      toast.success(saved > 20000 ? `Imagen subida y optimizada (−${Math.round(saved / 1024)} KB)` : "Imagen subida");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const preview = resolveImageUrl(value);

  return (
    <div data-testid={testid}>
      <label className="text-sm font-medium">{label}</label>
      {preview ? (
        <div className="relative mt-1 rounded-2xl overflow-hidden border border-black/10 group">
          <img src={preview} alt="portada" className="w-full h-44 object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 p-2 rounded-full bg-white/90 text-red-600 hover:bg-white shadow"
            data-testid={`${testid}-remove`}
            aria-label="Quitar imagen"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
          className={`mt-1 cursor-pointer rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 h-44 text-center px-4 transition-colors ${
            dragOver ? "border-[#2A00D9] bg-[#2A00D9]/5" : "border-black/15 hover:border-[#2A00D9]"
          }`}
          data-testid={`${testid}-dropzone`}
        >
          {uploading ? (
            <><Loader2 size={22} className="animate-spin text-[#2A00D9]" /><span className="text-sm text-[#4A4A4A]">Subiendo...</span></>
          ) : (
            <>
              <UploadCloud size={24} className="text-[#2A00D9]" />
              <span className="text-sm font-medium">Arrastra una imagen o haz clic para subir</span>
              <span className="text-xs text-[#4A4A4A]">JPG, PNG, WEBP o GIF</span>
            </>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {onAltChange && (
        <div className="mt-2">
          <input
            type="text"
            value={altValue || ""}
            onChange={(e) => onAltChange(e.target.value)}
            placeholder={altPlaceholder}
            className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm focus:outline-none focus:border-[#2A00D9]"
            data-testid={`${testid}-alt`}
          />
          <p className="text-[11px] text-[#4A4A4A] mt-1">Texto alternativo (alt). Si lo dejas vacío se usará el título automáticamente.</p>
        </div>
      )}
    </div>
  );
}
